import { gql } from "@apollo/client";
import { client } from "../apolloClient";

// GraphQL query to get user data
const GET_USER_DATA = gql`
  query GetUserData {
    currentUser {
      id
      name
      email
      role
      clientId
      client {
        clientId
        client_name
        folder
      }
      supplierId
      supplier {
        id
        name
        subfolder
        clientId
        client {
          clientId
          client_name
          folder
        }
      }
      createdAt
      updatedAt
      error {
        code
        message
      }
    }
  }
`;

export const getUserData = async (): Promise<any> => {
  try {
    // Read optional mock user id (used only in local/dev/testing flows)
    const userId = localStorage.getItem("userId");
    const clientId = localStorage.getItem("clientId");

    console.log("[getUserData] Starting user data fetch", { userId });

    // IMPORTANT: Do not early-return if userId is missing. In staging/prod,
    // authentication is established via Cloudflare/Okta and HTTP-only cookies.
    // We proceed to query currentUser; the auth cookie + CSRF should suffice.

    // CSRF tokens are handled automatically by the main Apollo client's csrfLink.
    // App.tsx also fetches the CSRF token before calling getUserData during init.

    // Query the server for user data using the main Apollo client, which includes
    // session error detection, magic link Bearer token auth, CSRF handling,
    // and credentials: "include" for cross-origin cookie support.
    const { data } = await client.query({
      query: GET_USER_DATA,
      fetchPolicy: "network-only",
    });

    if (!data || !data.currentUser) {
      return null;
    }

    // Check if there's an error in the response
    if (data.currentUser.error || data.currentUser.id === "error") {
      console.error(
        "User authentication error:",
        data.currentUser.error?.message || "User not found",
      );
      return {
        error: data.currentUser.error || {
          code: "USER_NOT_FOUND",
          message: "User does not exist or invalid credentials",
        },
      };
    }

    // Get client and supplier info
    let clientName = null;
    let supplierId = null;
    let supplierName = null;
    let supplierClientId = null;

    // Calculate folderPath from client and supplier data
    let folderPath = null;

    // For users with direct client association (CUSTOMER, ADMIN)
    if (data.currentUser.client) {
      clientName = data.currentUser.client.client_name;
      folderPath = data.currentUser.client.folder;
    }

    // For supplier users, get client info from the supplier's client relation
    if (data.currentUser.supplier) {
      supplierId = data.currentUser.supplierId;
      supplierName = data.currentUser.supplier.name;

      // Supplier has a client relation - use it to determine folder path and clientId
      if (data.currentUser.supplier.client) {
        supplierClientId = data.currentUser.supplier.client.clientId;
        clientName = data.currentUser.supplier.client.client_name;
        folderPath = data.currentUser.supplier.client.folder;

        // Combine with supplier subfolder
        if (data.currentUser.supplier.subfolder) {
          folderPath = `${folderPath}/${data.currentUser.supplier.subfolder}`;
        }
      }
    }

    // Determine permissions based on user role
    let canView = false;
    let canUpload = false;
    let canDelete = false;

    switch (data.currentUser.role) {
      case "ADMIN":
        // Admin has all permissions
        canView = true;
        canUpload = true;
        canDelete = true;
        break;
      case "CUSTOMER":
        // Customer has full permissions to their folder
        canView = true;
        canUpload = true;
        canDelete = true;
        break;
      case "SUPPLIER":
        // Supplier has full permissions to their subfolder
        canView = true;
        canUpload = true;
        canDelete = true;
        break;
      case "PORTWAY_EMPLOYEE":
        // Portway employees have view-only access
        canView = true;
        canUpload = false;
        canDelete = false;
        break;
      default:
        // Default to no permissions
        canView = false;
        canUpload = false;
        canDelete = false;
    }

    return {
      id: data.currentUser.id,
      name: data.currentUser.name,
      email: data.currentUser.email,
      role: data.currentUser.role,
      // For suppliers, use the client from supplier relation; otherwise use direct clientId
      clientId:
        data.currentUser.clientId || supplierClientId || clientId || null,
      clientName,
      supplierId,
      supplierName,
      folderPath,
      canView,
      canUpload,
      canDelete,
    };
  } catch (error) {
    console.error("Error fetching user data:", error);
    return {
      error: {
        code: "FETCH_ERROR",
        message:
          error instanceof Error ? error.message : "Failed to fetch user data",
      },
    };
  }
};
